/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import {
  RATES_DATA,
  RATES_DATA_FOR_SELECTED_PROPERTY,
  PROPERTY_INFO_DATA,
  SEARCH_RESULTS_ALERTS,
  SESSION_DATA_VALUES,
  NO_RATES_ARE_AVAILABLE,
  TOO_MANY_PROPERTIES,
} from '../constants';
import { deepCloneZustandState } from '../utils/helpers';

// Import SearchResultDataType for SearchResultContext migration
export interface SearchResultDataType {
  total?: number;
  pageInfo?: any;
  edges?: Array<any | null> | null;
  searchCenter?: {
    latitude: string;
    longitude: string;
    name: string;
    address: string;
  };
  showMapView: boolean;
  media?: any;
  facets?: Array<any>;
  currentOffset?: number;
  geoCodeInfo?: {
    details?: {
      destinationType?: string;
      distance?: number;
      location?: {
        address?: string;
        city?: string;
        country?: string;
        latitude?: number;
        longitude?: number;
        state?: string;
      };
    };
    placeId?: string;
  };
  llmMetadata?: {
    isLlmEngaged?: boolean;
    llmInfo?: {
      queryRewrite?: string;
      callTimes?: {
        dateCall?: string;
        locationCall?: string;
        priceCall?: string;
        queryRewrite?: string;
      };
      date?: {
        earliest?: string;
        latest?: string;
        length?: number;
        type?: string;
      };
      location?: {
        intent?: string;
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  messageUiData?: any;
}

export interface SearchResultsState {
  ratesData: any;
  ratesDataForSelectedProperty: any;
  propertyInfoData: any;
  searchResultsAlerts: any;
  sessionDataValues: any;
  noRatesAreAvailable: any;
  tooManyProperties: any;
  searchResultData: SearchResultDataType;
  setSearchResultsState: (fieldNamesArray: Array<string>, fieldsData: any, reset?: boolean) => any;
  setSearchResultData: (payload: Partial<SearchResultDataType>) => void;
  changeCurrentView: (showMapView: boolean) => void;
  updateMediaData: (media: any) => void;
  resetSearchResult: () => void;
  searchResultInitialization: (data: Partial<SearchResultDataType>) => void;
}

const initialSearchResultData: SearchResultDataType = {
  showMapView: false,
};

const initialState: SearchResultsState = {
  [RATES_DATA]: {
    [RATES_DATA]: {},
  },
  [RATES_DATA_FOR_SELECTED_PROPERTY]: {
    [RATES_DATA_FOR_SELECTED_PROPERTY]: {},
  },
  [PROPERTY_INFO_DATA]: {
    [PROPERTY_INFO_DATA]: {},
  },
  [SEARCH_RESULTS_ALERTS]: {
    [SEARCH_RESULTS_ALERTS]: {},
  },
  [SESSION_DATA_VALUES]: {
    [SESSION_DATA_VALUES]: {
      isFlexibleSearch: '',
      recentlyViewedPropertyCode: '',
    },
  },
  [NO_RATES_ARE_AVAILABLE]: {
    [NO_RATES_ARE_AVAILABLE]: {
      noRatesAvailableForAnyProperty: false,
    },
  },
  [TOO_MANY_PROPERTIES]: {
    [TOO_MANY_PROPERTIES]: {
      tooManyPorpertiesTriggered: false,
    },
  },
  searchResultData: initialSearchResultData,
  setSearchResultsState: () => {},
  setSearchResultData: () => {},
  changeCurrentView: () => {},
  updateMediaData: () => {},
  resetSearchResult: () => {},
  searchResultInitialization: () => {},
};

function searchResultsStateHelperFunction(
  state: Record<string, any>,
  fieldNames: Array<string>,
  fieldsData: Record<string, any>,
  reset?: boolean
): object {
  const updatedState = deepCloneZustandState(state);
  //to reset everything inside the given field , this will not reset the whole store but only reset the given fieldname
  if (reset) {
    fieldNames.forEach((name: string) => {
      updatedState[name] = {};
    });
  }
  fieldNames.forEach((name: string) => {
    updatedState[name] = { ...updatedState[name], ...fieldsData[name] };
  });
  return updatedState;
}

const SearchResultsStore: StateCreator<SearchResultsState> = set => {
  return {
    ...initialState,
    setSearchResultsState: (fieldNamesArray: Array<string>, fieldsData: any, reset?: boolean) =>
      set(state => {
        const updatedState = searchResultsStateHelperFunction(state, fieldNamesArray, fieldsData, reset);
        return updatedState;
      }),

    // SearchResultContext migration methods
    setSearchResultData: (payload: Partial<SearchResultDataType>) =>
      set(state => ({
        searchResultData: { ...state.searchResultData, ...payload },
      })),

    changeCurrentView: (showMapView: boolean) =>
      set(state => ({
        searchResultData: { ...state.searchResultData, showMapView },
      })),

    updateMediaData: (media: any) =>
      set(state => ({
        searchResultData: { ...state.searchResultData, media },
      })),

    resetSearchResult: () =>
      set(() => ({
        searchResultData: initialSearchResultData,
      })),

    searchResultInitialization: (data: Partial<SearchResultDataType>) =>
      set(() => ({
        searchResultData: { ...initialSearchResultData, ...data },
      })),
  };
};

export const useSearchResultsStore = createAppStore(SearchResultsStore, {
  usePersistentStore: false,
});
